"use client";

import Link from "next/link";
import Image from "next/image";
import { formatShortDate } from "@/lib/content";
import { MapPin, ArrowRight, Star } from "lucide-react";
import { motion } from "framer-motion";

interface EventCardProps {
  id: string;
  slug: string;
  title: string;
  date: string;
  type: string;
  image: string;
  description: string;
  location: string;
  featured?: boolean;
  redirectToNotFound?: boolean;
  index?: number;
  /** Affiche la carte en version "archive" : atténuée, avec l'année. */
  past?: boolean;
}

export default function EventCard({
  slug,
  title,
  date,
  type,
  image,
  description,
  location,
  featured = false,
  redirectToNotFound = false,
  index = 0,
  past = false,
}: EventCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true, margin: "-100px" }}
    >
      <Link
        href={
          redirectToNotFound ? `/events/${slug}-not-found` : `/events/${slug}`
        }
        className="block group relative bg-white rounded-xl shadow-md hover:shadow-xl 
               transition-all duration-300 overflow-hidden hover:-translate-y-1 
               max-w-md w-full"
      >
        {/* Badge Featured */}
        {featured && (
          <div className="absolute top-4 left-4 z-10 bg-c2e-red text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
            <Star className="h-2.5 w-2.5 fill-current" />
            À la une
          </div>
        )}

        {/* Image */}
        <div className="relative h-48 overflow-hidden">
          <Image
            src={image}
            alt={title}
            fill
            className={`object-cover group-hover:scale-110 transition-transform duration-300 ${
              past ? "grayscale group-hover:grayscale-0" : ""
            }`}
          />
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

          {/* Date badge */}
          <div className="absolute bottom-4 left-4 bg-white rounded-lg p-2 text-center shadow-md">
            {(() => {
              const short = formatShortDate(date); // e.g. "05 oct."
              const [day, month] = short.split(" ");
              return (
                <>
                  <p className="text-2xl font-bold text-c2e-blue">{day}</p>
                  <p className="text-xs text-c2e-gray uppercase">{month}</p>
                  {past && (
                    <p className="text-[10px] text-c2e-gray/70 font-medium">
                      {new Date(date).getFullYear()}
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Type */}
          <span className="inline-block bg-c2e-blue-lightest text-c2e-blue text-xs font-semibold px-3 py-1 rounded-full mb-3">
            {type}
          </span>

          {/* Title */}
          <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-c2e-blue transition-colors">
            {title}
          </h3>

          {/* Description */}
          <p className="text-sm text-gray-900 mb-4 line-clamp-2">
            {description}
          </p>

          {/* Location & CTA */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-gray-900">
              <MapPin className="h-4 w-4" />
              <span className="line-clamp-1">{location}</span>
            </div>
            <span className="text-c2e-blue font-semibold text-sm group-hover:gap-2 flex items-center gap-1 transition-all">
              Voir plus
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
